import { useMemo, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { useTranslation } from 'react-i18next';
import styles from './FilterPopover.module.css';

export interface FilterOption {
  id: string;
  label: string;
  hint?: string | undefined;
}

/**
 * Фильтр таблицы. В макете нарисован только сам переключатель, содержимое списка собрано
 * по токенам: поиск сверху, множественный выбор, сброс внизу. Выбранные значения показываются
 * числом в подписи кнопки — как в макете «Статус: 2».
 */
export function FilterPopover({
  name,
  options,
  selected,
  onChange,
  searchable = false,
  extraOption,
}: {
  name: string;
  options: FilterOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  searchable?: boolean;
  /** Дополнительный пункт вроде «Без исполнителя», не приходящий из справочника. */
  extraOption?: { id: string; label: string } | undefined;
}): React.ReactElement {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => option.label.toLowerCase().includes(needle));
  }, [options, search]);

  const active = selected.length > 0;

  function toggle(id: string): void {
    onChange(selected.includes(id) ? selected.filter((item) => item !== id) : [...selected, id]);
  }

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button type="button" className={`${styles.trigger} ${active ? styles.triggerActive : ''}`}>
          <span>{active ? t('filters.selected', { name, count: selected.length }) : name}</span>
          <span className={styles.chevron} aria-hidden="true">
            ▾
          </span>
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content className={styles.content} align="start" sideOffset={6}>
          {searchable && (
            <input
              className={styles.search}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('filters.search')}
              autoFocus
            />
          )}

          <div className={styles.list} role="group" aria-label={name}>
            {extraOption && (
              <label className={styles.option}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={selected.includes(extraOption.id)}
                  onChange={() => toggle(extraOption.id)}
                />
                <span className={`${styles.optionLabel} truncate`}>{extraOption.label}</span>
              </label>
            )}

            {visible.map((option) => (
              <label className={styles.option} key={option.id}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={selected.includes(option.id)}
                  onChange={() => toggle(option.id)}
                />
                <span className={`${styles.optionLabel} truncate`}>{option.label}</span>
                {option.hint !== undefined && (
                  <span className={`mono ${styles.optionHint}`}>{option.hint}</span>
                )}
              </label>
            ))}

            {visible.length === 0 && !extraOption && (
              <div className={styles.empty}>{t('filters.empty')}</div>
            )}
          </div>

          {active && (
            <button type="button" className={styles.reset} onClick={() => onChange([])}>
              {t('filters.reset')}
            </button>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
